import { updateClaimStatus } from 'feature/claims/claimSlice';
import { downloadClaims } from 'feature/claims/claimsSlice';
import { setDialogOpen, setInjectionState } from 'feature/hooks/dialogSlice';
import { useClaimHook } from 'hooks/claim.hook';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import { Button, Popover, PopoverButton, Stack } from 'styles';
import { CLAIM_ACTIONS } from 'types/Claim';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import ClaimPendingContent from './ClaimStatusChangeContent';
import ClaimVoidModalContent from './ClaimVoidModalContent.component';
import DialogRekey from './DialogRekey';
import ClaimReturnActionButtons from './components/ClaimReturn/ClaimReturnActionButtons.component';

const ClaimButtonList = () => {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const param = useParams<{ uuid: string }>();
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { claimData: claim } = useSelector((store: RootState) => store.claim);

	const {
		enableClaimReview,
		enableClaimRekey,
		enableClaimApprove,
		enableClaimReject,
		enableClaimPending,
		enableClaimVoid,
		enableClaimComplaint,
		enableClaimFraud,
		enableClaimReturn,
	} = useClaimHook();

	const showActions =
		enableClaimReview ||
		enableClaimRekey ||
		enableClaimApprove ||
		enableClaimReject ||
		enableClaimPending ||
		(enableClaimVoid && claim.claimInNetwork) ||
		enableClaimComplaint ||
		enableClaimFraud ||
		enableClaimReturn;

	const [openRekey, setOpenRekey] = useState<boolean>(false);

	const handleClick = (type: string) => {
		const claimUuid = param.uuid as string;
		dispatch(downloadClaims({ uuid: claimUuid, type: type }));
	};

	const hadleStatusClick = (approve: boolean) => {
		const claimUUID = param.uuid as string;
		dispatch(
			updateClaimStatus({ claimUuids: [claimUUID], statusId: approve ? 4 : 8 }),
		);
		handleClose();
	};

	const handleClickReviewClaim = () => {
		navigate(`/view-claims/${param.uuid}/review`);
		handleClose();
	};

	const [anchorEl, setAnchorEl] = useState<Element | null>(null);

	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<Stack
			direction={'row'}
			sx={{
				gap: 2,
				alignContent: 'center',
				justifyContent: 'flex-end',
			}}
		>
			{enableClaimReview && (
				<Button
					type="button"
					variant="outlined"
					fullWidth={false}
					onClick={() => handleClickReviewClaim()}
				>
					{tClientLabels('claim.button.review')}
				</Button>
			)}
			{claim.status.id !== 15 && (
				<Stack ml={'auto'}>
					<Button
						type="button"
						variant="contained"
						fullWidth={false}
						onClick={() => handleClick('PDF')}
					>
						{tClientLabels('claim.button.download')}
					</Button>
				</Stack>
			)}
			{showActions && (
				<Stack ml={'auto'}>
					<Button
						type="button"
						variant="contained"
						fullWidth={false}
						onClick={event => {
							setAnchorEl(event.currentTarget);
						}}
					>
						{tClientLabels('claim.button.action')}
					</Button>
				</Stack>
			)}
			<Popover open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}>
				{enableClaimApprove && (
					<PopoverButton
						type="button"
						variant="popoverButton"
						onClick={() => hadleStatusClick(true)}
					>
						{tClientLabels('commons.buttons.approve')}
					</PopoverButton>
				)}
				{enableClaimReject && (
					<PopoverButton
						type="button"
						variant="popoverButton"
						onClick={() => {
							dispatch(
								setInjectionState({
									title: 'reject.title',
									content: <ClaimPendingContent action={CLAIM_ACTIONS.REJECT} />,
									buttonClose: true,
								}),
							);
							dispatch(setDialogOpen(true));
							handleClose();
						}}
					>
						{tClientLabels('commons.buttons.reject')}
					</PopoverButton>
				)}
				{enableClaimReview && (
					<PopoverButton
						type="button"
						variant="popoverButton"
						onClick={() => handleClickReviewClaim()}
					>
						{tClientLabels('claim.button.review')}
					</PopoverButton>
				)}
				{enableClaimRekey && (
					<PopoverButton
						type="button"
						variant="popoverButton"
						onClick={() => {
							setOpenRekey(true);
							handleClose();
						}}
					>
						{tClientLabels('claim.button.rekey')}
					</PopoverButton>
				)}
				{enableClaimPending && (
					<PopoverButton
						type="button"
						variant="popoverButton"
						onClick={() => {
							dispatch(
								setInjectionState({
									title: 'pending.title',
									content: <ClaimPendingContent action={CLAIM_ACTIONS.PENDING} />,
									buttonClose: true,
								}),
							);
							dispatch(setDialogOpen(true));
							handleClose();
						}}
					>
						{tClientLabels('claim.button.pending')}
					</PopoverButton>
				)}
				{enableClaimVoid && (
					<PopoverButton
						type="button"
						variant="popoverButton"
						disabled={!claim.claimInNetwork}
						onClick={() => {
							dispatch(
								setInjectionState({
									title: `${TRANSLATION_KEYS.DIALOG}.confirm.title.reason`,
									content: <ClaimVoidModalContent />,
									buttonClose: true,
								}),
							);
							dispatch(setDialogOpen(true));
							handleClose();
						}}
					>
						{tClientLabels('claim.button.void')}
					</PopoverButton>
				)}
				{enableClaimComplaint && (
					<PopoverButton
						type="button"
						variant="popoverButton"
						onClick={() => {
							navigate(`/view-claims/${param.uuid}/add-complaint`);
						}}
					>
						{tClientLabels('claim.button.complaint')}
					</PopoverButton>
				)}
				{enableClaimFraud && (
					<PopoverButton
						type="button"
						variant="popoverButton"
						onClick={() => {
							navigate(`/view-claims/${param.uuid}/add-fraud`);
						}}
					>
						{tClientLabels('claim.button.fraud')}
					</PopoverButton>
				)}
				{enableClaimReturn && <ClaimReturnActionButtons />}
			</Popover>
			<DialogRekey claim={claim} open={openRekey} setOpen={setOpenRekey} />
		</Stack>
	);
};

export default ClaimButtonList;
